import React, { useEffect } from "react";
import ZylenHeader from "../components/landing/ZylenHeader";
import ZylenFooter from "../components/landing/ZylenFooter";
import "../components/landing/ZylenLanding.css";
import "../components/landing/LandingEvents.css";

import useEventTicketPayment from "../utils/useEventTicketPayment";
const photos = [
  [
    "event-crowd-colour.jpg",
    "a full room of filmmakers at a zylen monthly event",
  ],
  ["event-group-gallery.jpg", "a group photo under festoon lights"],
  ["event-group-wide.jpg", "everyone together at a zylen event"],
  ["event-mingling.jpg", "filmmakers meeting each other at a zylen event"],
];
const images = require.context(
  "../assets/landingpage/images/photos",
  false,
  /\.jpg$/,
);

function EventPaymentNotice({ payment, busy, onCheck }) {
  const cancelled = payment.status === "cancelled";
  const awaiting = ["pending", "unknown"].includes(payment.status);
  if (!cancelled && !awaiting) return null;
  const closed = cancelled || /cancel|closed/i.test(payment.message || "");
  const title = closed ? "Checkout closed" : "Payment confirmation pending";
  const description = cancelled
    ? "Your details are saved. You can try checkout again whenever you’re ready."
    : "Your payment hasn’t been confirmed yet. Check its status before starting another payment.";

  return (
    <div
      role="status"
      aria-live="polite"
      className={`my-5 rounded-2xl border p-4 text-left ${
        cancelled
          ? "border-[#dce8ee] bg-[#f2f7fa]"
          : "border-[#efdfb7] bg-[#fffbf0]"
      }`}
    >
      <div className="flex items-start gap-3">
        <span
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
            cancelled
              ? "bg-[#e2edf3] text-[#327691]"
              : "bg-[#faefcf] text-[#90691d]"
          }`}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="9" />
            {cancelled ? (
              <path d="m9 9 6 6m0-6-6 6" />
            ) : (
              <path d="M12 7v5l3 2" />
            )}
          </svg>
        </span>
        <div className="min-w-0 flex-1">
          <h4 className="m-0 text-sm font-bold leading-5 text-[#173746]">
            {title}
          </h4>
          <p className="mb-0 mt-1.5 break-words text-[13px] leading-[1.65] text-[#607481]">
            {description}
          </p>
          {awaiting && (
            <button
              type="button"
              disabled={busy}
              onClick={onCheck}
              className="mt-3 rounded-lg border border-[#ccdce4] bg-white px-3 py-2 text-xs font-semibold text-[#327691] disabled:opacity-60"
            >
              {busy ? "Checking…" : "Check payment status"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function LandingEvents() {
  const {
    quantity,
    setQuantity,
    form,
    change,
    submit,
    busy,
    price,
    error,
    payment,
    reset,
    retryVerification,
    availableSeats,
    soldOut,
    maxTickets,
    refreshAvailability,
  } = useEventTicketPayment();
  useEffect(() => {
    document.title = "zylen events";

    window.scrollTo({
      top: 0,
      behavior: "auto",
    });
  }, []);
  const scrollToBook = (e) => {
    e.preventDefault();
    document
      .getElementById("book")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <div className="zylen-v3 zylen-events-page">
      <ZylenHeader />
      <main id="top">
        <section className="ev-hero">
          <div className="wrap">
            <div>
              <span className="eyebrow">monthly events</span>
              <h1>join the next event.</h1>
              <p className="lead">
                80 to 100 filmmakers in one room, every month in Cape Town. A
                working filmmaker on stage, their work on the big screen, and
                the people you'll want on your next shoot
              </p>
              <div className="stores" style={{ marginTop: 30 }}>
                <a
                  className="btn solid big ev-white-button"
                  aria-disabled={soldOut || availableSeats === 0}
                  href="#book"
                  onClick={scrollToBook}
                >
                  {soldOut ? "seats full" : "book your ticket"}
                </a>
              </div>
            </div>
            <article className="next" aria-label="next event">
              <span className="tag">next event</span>
              <h2>zylen x Retropia event</h2>
              <dl>
                <div>
                  <dt>date</dt>
                  <dd>24 November 2026</dd>
                </div>
                <div>
                  <dt>time</dt>
                  <dd>7:00 pm to 10:00 pm</dd>
                </div>
                <div>
                  <dt>where</dt>
                  <dd>
                    Labia Theatre 68 Orange Street, Gardens,
                    <br className="md:block hidden" /> Cape Town
                  </dd>
                </div>
                <div>
                  <dt>ticket</dt>
                  <dd className="price">
                    {price === null
                      ? "Loading…"
                      : `R${price.toLocaleString("en-ZA")}`}
                  </dd>
                </div>
              </dl>
              <span className="spots" role="status" aria-live="polite">
                {soldOut
                  ? "Seats full — sold out"
                  : availableSeats === null
                    ? "Checking seats…"
                    : availableSeats === 0
                      ? "All remaining seats temporarily reserved"
                      : `${availableSeats} seat${availableSeats === 1 ? "" : "s"} available`}
              </span>
              <a className="btn solid big" href="#book" onClick={scrollToBook}>
                {soldOut ? "seats full" : "book your ticket"}
              </a>
            </article>
          </div>
        </section>

        <section className="sec book" id="book">
          <div className="wrap">
            <div className="sec-head">
              <span className="eyebrow">tickets</span>
              <h2 className="h2">book your ticket.</h2>
              <p className="lead">
                enter your details, choose your tickets, and pay securely with
                Paystack.
              </p>
            </div>
            {availableSeats === 0 && payment.status !== "paid" && (
              <div
                role="status"
                className="mb-6 rounded-2xl border border-[#dce8ee] bg-[#f2f7fa] p-5 text-[#173746]"
              >
                <h3 className="m-0 text-lg font-bold">
                  {soldOut ? "Seats full" : "Seats temporarily reserved"}
                </h3>
                <p className="mb-0 mt-2 text-sm leading-6">
                  {soldOut
                    ? "The event is sold out. Ticket booking is now closed."
                    : "Other guests are completing checkout. Seats may become available if their payments are not completed."}
                </p>
                {!soldOut && (
                  <button
                    type="button"
                    onClick={refreshAvailability}
                    className="mt-3 rounded-xl border border-[#ccdce4] bg-white px-4 py-2 text-sm font-semibold text-[#327691]"
                  >
                    Check availability
                  </button>
                )}
              </div>
            )}
            {payment.status === "requires_review" && (
              <div
                role="alert"
                className="mb-6 rounded-2xl border border-[#efdfb7] bg-[#fffbf0] p-5 text-[#173746]"
              >
                <h3 className="m-0 text-lg font-bold">
                  Please contact the event team
                </h3>
                <p className="mb-0 mt-2 text-sm leading-6">{payment.message}</p>
                <p className="mb-0 mt-2 break-all text-xs">
                  Reference: {payment.reference}
                </p>
              </div>
            )}
            <form className="book-grid" onSubmit={submit} noValidate>
              <div className="panel">
                <h3>your details</h3>
                <div className="fields">
                  <div className="field">
                    <label htmlFor="ev-fn">first name</label>
                    <input
                      id="ev-fn"
                      name="first_name"
                      autoComplete="given-name"
                      required
                      value={form.first_name}
                      onChange={change}
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="ev-ln">last name</label>
                    <input
                      id="ev-ln"
                      name="last_name"
                      autoComplete="family-name"
                      required
                      value={form.last_name}
                      onChange={change}
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="ev-em">email</label>
                    <input
                      id="ev-em"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={form.email}
                      onChange={change}
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="ev-ph">
                      phone <span>(optional)</span>
                    </label>
                    <input
                      id="ev-ph"
                      name="phone"
                      type="tel"
                      autoComplete="tel"
                      placeholder="+27"
                      value={form.phone}
                      onChange={change}
                    />
                  </div>
                  <div className="field full">
                    <label htmlFor="ev-role">what do you do?</label>
                    <select
                      id="ev-role"
                      name="role"
                      value={form.role}
                      onChange={change}
                    >
                      {[
                        "filmmaker",
                        "director",
                        "video editor",
                        "photographer",
                        "producer",
                        "actor or presenter",
                        "business or brand",
                        "student",
                        "something else",
                      ].map((role) => (
                        <option key={role}>{role}</option>
                      ))}
                    </select>
                  </div>
                  <div className="field full">
                    <label>tickets</label>
                    <div className="stepper">
                      <button
                        type="button"
                        aria-label="one less ticket"
                        disabled={busy || quantity <= 1 || maxTickets === 0}
                        onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                      >
                        −
                      </button>
                      <output aria-live="polite">{quantity}</output>
                      <button
                        type="button"
                        aria-label="one more ticket"
                        disabled={busy || quantity >= maxTickets}
                        onClick={() =>
                          setQuantity((q) => Math.min(maxTickets, q + 1))
                        }
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
                {availableSeats !== null &&
                  maxTickets > 0 &&
                  quantity > maxTickets && (
                    <p role="status" className="mt-3 text-sm text-[#90691d]">
                      Only {maxTickets} ticket(s) are currently available.
                      Please reduce your quantity.
                    </p>
                  )}
                <div className="checks">
                  <label className="check">
                    <input
                      type="checkbox"
                      name="marketing_opt_in"
                      checked={form.marketing_opt_in}
                      onChange={change}
                    />
                    <span>
                      <b>keep me posted.</b> send me the next event dates and
                      zylen news by email and app notifications. you can
                      unsubscribe any time.
                    </span>
                  </label>
                  <label className="check">
                    <input
                      type="checkbox"
                      name="terms"
                      required
                      checked={form.terms}
                      onChange={change}
                    />
                    <span>
                      I agree to the{" "}
                      <a
                        href="#ticket-terms"
                        onClick={(e) => e.preventDefault()}
                      >
                        ticket terms
                      </a>{" "}
                      and the{" "}
                      <a
                        href="#privacy-policy"
                        onClick={(e) => e.preventDefault()}
                      >
                        privacy policy
                      </a>
                      .
                    </span>
                  </label>
                  {error && (
                    <p className="err show" role="alert">
                      {error}
                    </p>
                  )}
                </div>
              </div>
              <aside className="panel summary" aria-label="order summary">
                {payment.status === "paid" ? (
                  <div className="done show" role="status">
                    <div className="tick">✓</div>
                    <h3>payment successful.</h3>
                    <p>
                      Your booking for {payment.quantity} ticket(s) is
                      confirmed. Total: R
                      {payment.amount?.toLocaleString("en-ZA")}. The event team
                      will receive your booking details by email.
                    </p>
                    <p>Reference: {payment.reference}</p>
                    <button
                      type="button"
                      className="btn solid big"
                      onClick={reset}
                    >
                      book more tickets
                    </button>
                  </div>
                ) : (
                  <div>
                    <h3>your order</h3>
                    <EventPaymentNotice
                      payment={payment}
                      busy={busy}
                      onCheck={retryVerification}
                    />
                    <div className="ev-sum-rows">
                      <div className="sum-row">
                        <span>early bird ticket</span>
                        <b>
                          {price === null
                            ? "Loading…"
                            : `R${price.toLocaleString("en-ZA")}`}
                        </b>
                      </div>
                      <div className="sum-row">
                        <span>tickets</span>
                        <b>{quantity}</b>
                      </div>
                    </div>
                    <div className="sum-total">
                      <span>total</span>
                      <span>
                        {price === null
                          ? "—"
                          : `R${(price * quantity).toLocaleString("en-ZA")}`}
                      </span>
                    </div>
                    <button
                      className="btn solid big ev-pay-button"
                      type="submit"
                      disabled={
                        busy ||
                        price === null ||
                        payment.status === "pending" ||
                        payment.status === "unknown" ||
                        payment.status === "requires_review" ||
                        availableSeats === null ||
                        availableSeats === 0 ||
                        quantity > maxTickets
                      }
                      style={
                        busy ||
                        price === null ||
                        availableSeats === null ||
                        availableSeats === 0 ||
                        quantity > maxTickets ||
                        ["pending", "unknown", "requires_review"].includes(
                          payment.status,
                        )
                          ? {
                              opacity: 0.6,
                              cursor: busy ? "wait" : "not-allowed",
                            }
                          : undefined
                      }
                    >
                      {busy
                        ? "please wait…"
                        : soldOut
                          ? "seats full"
                          : availableSeats === 0
                            ? "seats temporarily reserved"
                            : payment.status === "requires_review"
                              ? "contact the event team"
                              : payment.status === "cancelled"
                                ? "try payment again"
                                : "continue to payment"}
                    </button>
                    <p className="secure">
                      secure checkout powered by Paystack
                    </p>
                  </div>
                )}
              </aside>
            </form>
          </div>
        </section>
        <section className="sec night on-paint">
          <div className="wrap">
            <div className="sec-head">
              <span className="eyebrow">on the night</span>
              <h2 className="h2">what happens.</h2>
            </div>
            <div className="steps">
              <article className="step">
                <h3>arrive and meet</h3>
                <p>grab a drink and meet the room.</p>
              </article>
              <article className="step">
                <h3>on stage</h3>
                <p>a filmmaker shows their work and how they made it.</p>
              </article>
              <article className="step">
                <h3>your questions</h3>
                <p>ask the things you can use on your own sets.</p>
              </article>
              <article className="step">
                <h3>find your crew</h3>
                <p>time to meet the people you'll work with next.</p>
              </article>
            </div>
            <div className="mosaic">
              {photos.map(([filename, alt], i) => (
                <figure key={filename} className={`m${i + 1}`}>
                  <img src={images(`./${filename}`)} alt={alt} />
                </figure>
              ))}
            </div>
          </div>
        </section>
      </main>
      <ZylenFooter />
    </div>
  );
}
